import { AxiosResponse } from "axios";
import { TFunction } from "i18next";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { SeverityEnum } from "../../shared/enum/SeverityEnum";
import { FileUtils } from "../../shared/utils/FileUtils";
import { Image } from "../models/Image";
import { AdminImageService } from "../services/AdminImageService";
import { RichText } from "prismic-reactjs";
import { FormService } from "../../form/services/FormService";

interface Props extends WithTranslation {
}

interface States {
  images: Image[];
  displayConfirmation: boolean;
  toDelete: string | null;
}

class AdminImages extends React.Component<Props, States> {
  private readonly imageService: AdminImageService;
  private toast: Toast | null;
  private fileUpload: FileUpload | null;
  private t: TFunction;
  private formService: FormService;

  constructor(props: Props) {
    super(props);
    this.imageService = new AdminImageService();
    this.t = this.props.t;
    this.toast = null;
    this.fileUpload = null;
    this.formService = new FormService();
    this.state = {
      images: [],
      displayConfirmation: false,
      toDelete: null,
    };
  }

  componentDidMount() {
    this.getImages();
  }

  /**
   * Refresh decision trees
   */
  getImages() {
    this.imageService
      .getMany()
      .then((response: AxiosResponse<Image[]>) => {
        this.setState({ images: response.data });
      });
  }

  /**
   * On upload
   */
  handleUpload(e: { files: File[] }) {
    if (!e.files) {
      return;
    }
    this.imageService
      .uploadMany(e.files)
      .then(() => {
        this.toast?.show({
          severity: SeverityEnum.SUCCESS,
          detail: this.formService.t(this.t, "ui_admin_image_upload_toast_success", false),
        });
        this.fileUpload?.clear();
        this.getImages();
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message;
        this.toast?.show({
          severity: SeverityEnum.ERROR,
          detail: `${this.formService.t(this.t, "ui_admin_image_upload_toast_error", false)} : ${message}`,
        });
      });
  }

  /**
   * On download
   */
  handleDownload(name: string) {
    this.imageService
      .downloadOne(name)
      .then((response: AxiosResponse<Blob>) => {
        FileUtils.downloadFile(response);
      });
  }

  /**
   * Delete decision tree
   */
  handleDelete() {
    if (!this.state.toDelete) {
      return;
    }

    this.imageService.deleteOne(this.state.toDelete).then(() => {
      this.toast?.show({
        severity: SeverityEnum.SUCCESS,
        detail: this.t("ui_admin_image_delete_toast_success"),
      });
      this.setState({ displayConfirmation: false, toDelete: null });
      this.getImages();
    });
  }

  renderDialogFooter() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({ displayConfirmation: false })}
          className="p-button-text"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => this.handleDelete()}
          autoFocus
        />
      </div>
    );
  }

  actionBodyTemplate(rowData: Image) {
    return (
      <React.Fragment>
        <Button
          type="button"
          icon="pi pi-download"
          className="p-button-secondary p-mr-1"
          onClick={() => this.handleDownload(rowData.name)}
        ></Button>
        <Button
          type="button"
          icon="pi pi-trash"
          className="p-button-danger"
          onClick={() =>
            this.setState({ displayConfirmation: true, toDelete: rowData.name })
          }
        ></Button>
      </React.Fragment>
    );
  }

  render() {
    return (
      <Card title={this.formService.t(this.t, "ui_admin_image_title", false)}>
        <Toast ref={(el) => (this.toast = el)}/>
        <div className="p-grid">
          <div className="p-col-12">
            <FileUpload
              ref={(el) => (this.fileUpload = el)}
              accept="image/*"
              customUpload={true}
              multiple={true}
              uploadHandler={(e: { files: File[] }) => this.handleUpload(e)}
              // emptyTemplate={() => <p className="p-m-0">{this.t("ui_admin_image_upload_content")}</p>}
            ></FileUpload>
          </div>
          <div className="p-col-12">
            <DataTable
              value={this.state.images}
              rows={5}
              paginator={true}
              rowsPerPageOptions={[5, 10, 20]}
            >
              <Column
                field="name"
                filter
                filterPlaceholder={RichText.asText(this.t(
                  "ui_admin_image_search_by_name",
                  { returnObjects: true }
                ))}
                header={
                  this.formService.checkIfValue(RichText.render(this.t("ui_admin_image_name", { returnObjects: true })), "ui_admin_image_name")
                }
              />
              <Column
                body={(rowData: Image) =>
                  this.actionBodyTemplate(rowData)
                }
                headerStyle={{ width: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
              ></Column>
            </DataTable>
            <Dialog
              header="Confirmation"
              visible={this.state.displayConfirmation}
              modal
              style={{ width: "350px" }}
              footer={this.renderDialogFooter()}
              onHide={() => this.setState({ displayConfirmation: false })}
            >
              <div className="confirmation-content">
                <i
                  className="pi pi-exclamation-triangle p-mr-3"
                  style={{ fontSize: "2rem" }}
                />
                <span>Are you sure you want to proceed?</span>
              </div>
            </Dialog>
          </div>
        </div>
      </Card>
    );
  }
}

export default withTranslation()(AdminImages);
